import React, { Fragment, useState } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import FormForgot from 'components/FormForgot';
import { appVersion, productAuthor } from 'config';
import { useAuth } from 'context/AuthContext';
import Loading from 'components/Loading';
import TextTransitionInterval from 'components/TextTransitionInterval';
import FormLogin from 'components/FormLogin';
import FormOneTimePassword from 'components/FormOneTimePassword';
import LogoHorizontalWhite from 'assets/images/logo-horizontal-white.svg';
import LogoHorizontal from 'assets/images/logo-horizontal.svg';
import IconLoader from '../../components/IconLoader';
import Icon from '../../components/Icon';
import Alert from '../../components/Alert';
import useCustomCompany from '../../hooks/useCustomCompany';

const Wrapper = styled.div`
  display: flex;
  min-height: 100vh;
`;

const Welcome = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding: 100px;
  color: ${props => props.theme.colors.white};
  background: linear-gradient(-20deg, #ff6b00, #ffc24d);
`;

const WelcomeLogo = styled.div`
  width: 350px;
  margin-bottom: 30px;
  padding-bottom: 10px;
  border-bottom: 5px solid ${props => props.theme.colors.white};
`;

const WelcomeLogoImg = styled.img`
  width: 100%;
`;

const WelcomeText = styled.div`
  font-size: 64px;
  font-weight: ${props => props.theme.fontWeights.bold};
  line-height: 1.2;
  text-transform: uppercase;
`;

const WelcomeSubtext = styled.div`
  font-size: 64px;
  font-weight: ${props => props.theme.fontWeights.light};
  line-height: 1.2;
  text-transform: uppercase;
`;

const Sidebar = styled.div`
  flex: none;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  position: relative;
  width: 400px;
`;

const SidebarContent = styled.div`
  margin: auto;
  padding: 16px 40px;
`;

const SidebarLogo = styled.div`
  width: 240px;
  margin: auto;
`;

const SidebarLogoImg = styled.img`
  width: 100%;
`;

const AlertStyled = styled(Alert)`
  font-size: ${props => props.theme.fontSizes.sm};
  margin-bottom: 32px;
`;

const FormHeader = styled.div`
  margin: 20px 0 40px 0;
  padding: 0 20px;
  text-align: center;
  font-size: 14px;
  color: #bebebe;
`;

const FormLink = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  margin-top: 16px;
  color: ${props => props.theme.colors.primary};
  cursor: pointer;
  &:hover {
    text-decoration: underline;
  }
`;

const Copyright = styled.footer`
  flex: none;
  margin-bottom: 10px;
  text-align: center;
  font-size: 12px;
  color: #828282;
`;

const Login = props => {
  const { texts } = props;
  const { loginWithUsernameAndPassword, loginWithOneTimePassword, requestPassword } = useAuth();
  const { showForgotButton } = useCustomCompany();
  const [loading, setLoading] = useState(false);
  const [forgot, setForgot] = useState(false);
  const [error, setError] = useState();
  const [success, setSuccess] = useState();
  const [otpFields, setOtpFields] = useState(null);

  const doLogin = ({ loginFn, loginData }) => {
    setLoading(true);
    loginFn(loginData).catch(error => {
      const { status, data } = error;
      setLoading(false);
      setError(data?.message || 'No se ha podido establecer una conexión con el servidor');
      if (status === 400) {
        setOtpFields(loginData);
        setSuccess(null);
      }
    });
  };

  const doRequestPassword = ({ loginFn, loginData }) => {
    setLoading(true);
    loginFn(loginData)
      .then(data => {
        setLoading(false);
        setForgot(false);
        setSuccess(data?.message);
      })
      .catch(error => {
        const { data } = error;
        setLoading(false);
        setError(data?.message || 'No se ha podido establecer una conexión con el servidor');
      });
  };

  const handleLoginWithUsernameAndPassword = data => {
    doLogin({ loginFn: loginWithUsernameAndPassword, loginData: data });
  };

  const handleLoginWithOneTimePassword = data => {
    doLogin({ loginFn: loginWithOneTimePassword, loginData: data });
  };

  const handleRequestPassword = data => {
    doRequestPassword({ loginFn: requestPassword, loginData: data });
  };

  const handleForgotClick = () => {
    setForgot(true);
    setSuccess(null);
  };

  const handleForgotCancelClick = () => {
    setForgot(false);
  };

  const renderContent = () => {
    if (otpFields) {
      return (
        <Fragment>
          <FormHeader>
            Su contraseña es de un solo uso y debe cambiarla para poder acceder a la plataforma
          </FormHeader>
          <FormOneTimePassword
            username={otpFields.username}
            password={otpFields.password}
            onSubmit={handleLoginWithOneTimePassword}
            feedback={error}
          />
        </Fragment>
      );
    }

    if (forgot) {
      return (
        <Fragment>
          <FormHeader>
            Introduce tu usuario completo para solicitar un cambio de contraseña
          </FormHeader>
          <FormForgot onSubmit={handleRequestPassword} feedback={error} />
          <FormLink onClick={handleForgotCancelClick}>
            <Icon size={18}>
              <IconLoader name="back-icon.svg" />
            </Icon>
            <span>Cancelar</span>
          </FormLink>
        </Fragment>
      );
    }

    return (
      <Fragment>
        <FormHeader>Introduce tu usuario y contraseña para acceder a la plataforma</FormHeader>
        <FormLogin onSubmit={handleLoginWithUsernameAndPassword} feedback={error} />
        {showForgotButton && (
          <FormLink onClick={handleForgotClick}>
            <span>He olvidado la contraseña</span>
            <Icon size={18}>
              <IconLoader name="lock-1-icon.svg" />
            </Icon>
          </FormLink>
        )}
      </Fragment>
    );
  };

  return (
    <Wrapper>
      <Welcome>
        <WelcomeLogo>
          <WelcomeLogoImg src={LogoHorizontalWhite} />
        </WelcomeLogo>
        <WelcomeText>Gestiona</WelcomeText>
        <WelcomeSubtext>
          <TextTransitionInterval texts={texts} />
        </WelcomeSubtext>
      </Welcome>
      <Sidebar>
        {loading && <Loading />}
        <SidebarContent>
          {success && <AlertStyled state="success">{success}</AlertStyled>}
          <SidebarLogo>
            <SidebarLogoImg src={LogoHorizontal} />
          </SidebarLogo>
          {renderContent()}
        </SidebarContent>
        <Copyright>
          © {productAuthor}. v{appVersion}
        </Copyright>
      </Sidebar>
    </Wrapper>
  );
};

Login.propTypes = {
  texts: PropTypes.arrayOf(PropTypes.string).isRequired,
};

Login.defaultProps = {
  texts: [
    'tus horarios',
    'tus turnos',
    'tus fichajes',
    'tu plantilla',
    'tus jornadas',
    'tus servicios',
    'tus descansos',
    'tus empleados',
  ],
};

export default Login;
